import { createSelector } from 'reselect';
import { parse } from 'qs';
import { utils } from '@saucelabs/sl-module-performance';

import { filterCommands } from './utils';

const job = (store) => store.apps.job;
const performance = (store) => store.apps.performance;
const location = (store) => store.router.location;

const containerSelector = createSelector([
  job,
  (store) => store.user,
],
(filteredState, user) => ({
  loading: filteredState.container.get('loading'),
  notifications: filteredState.container.get('notifications'),

  commands: filteredState.job.get('commands'),
  job: filteredState.job.get('job'),
  user,
}));

const WebdriverContainerSelector = createSelector([
  (store) => store.experiments,
  job,
  (store) => store.user,
  location,
  performance,
],
(experiments, filteredState, user, routerLocation, performanceStore) => ({
  auth: parse(routerLocation.search, { ignoreQueryPrefix: true }).auth,
  columnsRatio: filteredState.container.get('ratio'),
  loading: filteredState.container.get('loading'),
  player: filteredState.container.get('player'),

  filteredCommands: filterCommands({
    commands: filteredState.job.get('commands'),
    filterState: filteredState.container.get('commandsFilter'),
    performanceData: performanceStore.jobPerformance.get('rawPerformanceData'),
    performanceRegressions: performanceStore.regression.get('performanceRegressions'),
  }),
  assets: filteredState.job.get('assets'),
  commandsLoadStatus: filteredState.job.get('commandsLoadStatus'),
  job: filteredState.job.get('job'),
  selectorsMapping: filteredState.job.get('selectorsMapping'),
  build: filteredState.job.get('build'),
  isVideoReady: filteredState.job.get('assetListFetched'),

  files: filteredState.files.get('files'),
  selectedFilename: filteredState.files.get('selectedFilename'),
  logFetchStatus: filteredState.files.get('status'),
  user,
  isOpenSauceUser: !user.isAuthenticated(),
  isJobOwner: user.username === filteredState.job.getIn(['job', 'owner']),
  regressionData: performanceStore.regression.get('performanceRegressions'),
  regressionOverview: utils.getRegressionOverview(performanceStore.regression.get('performanceRegressions')),
  loaderId: performanceStore.jobPerformance.get('loaderId'),
  routerSearchQuery: routerLocation.search,
  performanceAccessType: performanceStore.access.get('accessType'),
}));

const performanceAccessSelector = createSelector([
  (store) => store.apps.performance.access,
], (performanceAccess) => ({
  performanceAccessLoading: performanceAccess.get('loading'),
  performanceAccessType: performanceAccess.get('accessType'),
  performanceAccessError: performanceAccess.get('failure'),
}));

const WebdriverLivePreviewContainerSelector = createSelector(
  job,
  (store) => store.user,
  (filteredState, user) => ({
    job: filteredState.job.get('job'),
    build: filteredState.job.get('build'),
    isFreeUser: user.isFreeUser(),
    isOpenSauceUser: !user.isAuthenticated(),
  }),
);

const PublicRestrictedContainerSelector = createSelector(
  job,
  (store) => store.user,
  (filteredState, user) => ({
    build: filteredState.job.get('build'),
    columnsRatio: filteredState.container.get('ratio'),
    job: filteredState.job.get('job'),
    isOpenSauceUser: !user.isAuthenticated(),
    player: filteredState.container.get('player'),
  }),
);

const espressoContainerSelector = WebdriverContainerSelector;

const headlessContainerSelector = createSelector([
  WebdriverContainerSelector,
  job,
],
(webdriverSelectorState, filteredState) => ({
  ...webdriverSelectorState,
  galleryFullScreen: filteredState.container.get('galleryFullScreen'),
}));

const playerSelector = (state) => job(state).container.get('player');

const allCommandsSelector = createSelector(job, (jobState) => jobState.job.get('commands'));

// Respects commandsFilter
const filteredCommandsSelector = createSelector(job, performance, (
  jobState, performanceStore,
) => filterCommands({
  commands: jobState.job.get('commands'),
  filterState: jobState.container.get('commandsFilter'),
  performanceData: performanceStore.jobPerformance.get('rawPerformanceData'),
  performanceRegressions: performanceStore.regression.get('performanceRegressions'),
}));

// Respects commandsFilter
const commandSelector = (id) => (state) => filteredCommandsSelector(state)
  .find((command) => command.id === id);

const appiumContainerSelector = WebdriverContainerSelector;

const rawJobContainerSelector = createSelector(
  job,
  (filteredState) => ({ rawJob: filteredState.rawJob }),
);

export default containerSelector;
export {
  WebdriverContainerSelector,
  WebdriverLivePreviewContainerSelector,
  PublicRestrictedContainerSelector,
  allCommandsSelector,
  appiumContainerSelector,
  commandSelector,
  containerSelector,
  espressoContainerSelector,
  filteredCommandsSelector,
  headlessContainerSelector,
  job,
  playerSelector,
  rawJobContainerSelector,
  performanceAccessSelector,
  location,
};
